import { Timestamp } from "firebase/firestore";
import { ChatRoomType, ExistingChatRoom } from "../models/chat_room";
import { DbWrite } from "../database";
import { createToastMessageID } from "../utils";
import * as strings from "../strings";
import { CHAT_URL } from "../urls";
import { useUserContextStore } from "../store/user-context";

/**
 * Creates a new chat room or returns an existing one
 */
export async function createOrFindChatRoom({
  firebaseUser,
  selectedUserIds,
  myChatRooms,
  userDisplayNamesMap,
  siteKey,
  groupName,
  onSuccess,
  onError,
  canCreateGroupChats = true,
}: {
  firebaseUser: any;
  selectedUserIds: string[];
  myChatRooms: ExistingChatRoom[];
  userDisplayNamesMap: Record<string, string>;
  siteKey: string;
  groupName?: string;
  onSuccess: (chatRoomId: string) => void;
  onError: (message: string) => void;
  canCreateGroupChats?: boolean;
}): Promise<void> {
  try {
    if (!firebaseUser) {
      onError("You must be logged in to start a chat");
      return;
    }

    if (selectedUserIds.length === 0) {
      onError(strings.NO_USER_SELECTED);
      return;
    }

    // Include current user in the chat
    const allUserIds = Array.from(
      new Set([...selectedUserIds, firebaseUser.uid]),
    );
    const isGroupChat = allUserIds.length > 2;

    // Check if user has permission to create group chats
    if (isGroupChat && !canCreateGroupChats) {
      onError("Only site administrators can create group chats");
      return;
    }

    // Check if room already exists
    let existingRoom: ExistingChatRoom | undefined;

    if (!isGroupChat) {
      // For direct chats, check if a room with these exact users exists
      existingRoom = myChatRooms.find(
        (room) =>
          room.type === ChatRoomType.DIRECT &&
          room.userIds.length === 2 &&
          room.userIds.includes(firebaseUser.uid) &&
          room.userIds.includes(selectedUserIds[0]),
      );
    } else {
      // For group chats, check if a room with these exact users exists
      existingRoom = myChatRooms.find((room) => {
        // Must be a group chat with the exact same number of users
        if (
          room.type !== ChatRoomType.GROUP ||
          room.userIds.length !== allUserIds.length
        ) {
          return false;
        }

        // Check if all users in allUserIds are in this room's userIds
        // Sort both arrays for consistent comparison
        const sortedRoomUserIds = [...room.userIds].sort();
        const sortedAllUserIds = [...allUserIds].sort();

        // Compare each user ID to ensure exact match
        return sortedRoomUserIds.every(
          (userId, index) => userId === sortedAllUserIds[index],
        );
      });
    }

    if (existingRoom) {
      // Use existing room
      onSuccess(existingRoom.id);
    } else {
      // Create a name for the chat room
      const chatRoomName = isGroupChat
        ? groupName || "Group Chat"
        : userDisplayNamesMap[selectedUserIds[0]] || "Chat";

      // Create a new chat room
      const newChatRoom = {
        createdBy: firebaseUser.uid,
        lastModifiedBy: firebaseUser.uid,
        name: chatRoomName,
        type: isGroupChat ? ChatRoomType.GROUP : ChatRoomType.DIRECT,
        userIds: allUserIds,
        timestampCreated: Timestamp.now(),
        timestampLastModified: Timestamp.now(),
        metadata: {},
        lastMessage: null,
        imageUrl: null,
        craftRecordID: null,
      };

      // Save the chat room to the database
      const newRoomRef = await DbWrite.chatRoom.add(siteKey, newChatRoom);

      if (newRoomRef) {
        // Set the selected room ID to the new room
        onSuccess(newRoomRef.id);
      }
    }
  } catch (error) {
    console.warn("Error creating chat room", error);
    onError("Failed to create chat room");
  }
}

/**
 * Navigates to a chat room, either in the full-screen view or within the chat widget
 * based on the current location.
 *
 * @param roomId The ID of the room to navigate to
 * @param navigate React Router's navigate function (optional, used only for full-screen mode)
 * @param setSelectedRoomID Function to set the selected room ID in widget mode (optional)
 * @param setActiveChatRoomId Function to set the active room ID in the global store (optional)
 * @param forceFullScreen Whether to force navigation to the full-screen view (optional, defaults to false)
 */
export function navigateToRoom(
  roomId: string,
  navigate?: (path: string) => void,
  setSelectedRoomID?: (roomId: string) => void,
  setActiveChatRoomId?: (roomId: string) => void,
  forceFullScreen: boolean = false,
): void {
  // Check if we're in the full-screen chat view
  const isFullScreenChat =
    window.location.pathname === CHAT_URL ||
    window.location.pathname.startsWith(`${CHAT_URL}/`);

  if (isFullScreenChat) {
    // Already in chat view, just update the state
    if (setActiveChatRoomId) {
      setActiveChatRoomId(roomId);
    }

    if (setSelectedRoomID) {
      setSelectedRoomID(roomId);
    }
  } else if (navigate && forceFullScreen) {
    // Only navigate to full-screen if forceFullScreen is true
    navigate(CHAT_URL);

    // Also update the state
    if (setActiveChatRoomId) {
      setActiveChatRoomId(roomId);
    }

    if (setSelectedRoomID) {
      setSelectedRoomID(roomId);
    }
  } else {
    // Widget mode - just update the state
    if (setSelectedRoomID) {
      setSelectedRoomID(roomId);
    }

    // Update the global store for consistency
    if (setActiveChatRoomId) {
      setActiveChatRoomId(roomId);
    }
  }
}

/**
 * Gets context metadata for chat messages from the user context store
 * @returns An object with context metadata based on user's recent actions
 */
export function getContextMetadata(): { [key: string]: any } {
  const { contextHistory } = useUserContextStore.getState();

  // Add the context history to the metadata
  const metadata: { [key: string]: any } = {
    context: contextHistory,
  };

  return metadata;
}
